import { AuthorizationRequirement } from '../../models/authorization-requirement';
import { User, UserRole } from '../../models/user';
import { FeatureFlag } from '../../models/featureFlags';
import { matchesAuthorizationRequirement } from '../../utils/authorization.util';

export class NavItem {
  url: string;

  title: string;

  icon: string;

  allowedRoles?: UserRole[];

  authorizationRequirement?: AuthorizationRequirement;

  requiredFeatureFlags?: FeatureFlag[];

  active = true;

  // Only one level of nesting is supported
  items?: NavItem[];

  alternativeUrls?: string[];

  expanded?: boolean;

  constructor(
    url: string,
    title: string,
    icon: string,
    allowedRolesOrRequirement?: UserRole[] | AuthorizationRequirement,
    requiredFeatureFlags?: FeatureFlag[],
    items?: NavItem[],
    alternativeUrls?: string[],
    expanded?: boolean,
  ) {
    this.url = url;
    this.title = title;
    this.icon = icon;
    if (Array.isArray(allowedRolesOrRequirement)) {
      this.allowedRoles = allowedRolesOrRequirement;
    } else {
      this.authorizationRequirement = allowedRolesOrRequirement;
    }
    this.requiredFeatureFlags = requiredFeatureFlags;
    this.items = items;
    this.alternativeUrls = alternativeUrls;
    this.expanded = expanded;
  }

  setActive(url: string) {
    if (url === this.url) {
      this.active = true;
      return;
    }
    if (this.alternativeUrls && this.alternativeUrls.includes(url)) {
      this.active = true;
      return;
    }
    if (this.items) {
      this.items.forEach((item) => {
        item.setActive(url);
      });
    }
    this.active = false;
  }

  setExpanded(expanded: boolean) {
    this.expanded = expanded;
  }

  isVisible(user?: User): boolean {
    const hasRoleOrPermissionRequirement = !!this.allowedRoles?.length || !!this.authorizationRequirement;
    const hasFeatureFlagRequirement = !!this.requiredFeatureFlags?.length;

    if (!hasRoleOrPermissionRequirement && !hasFeatureFlagRequirement) {
      return true;
    }

    if (!user) {
      return false;
    }

    const isAuthorized = !hasRoleOrPermissionRequirement || matchesAuthorizationRequirement(user, {
      anyRoles: this.allowedRoles ?? this.authorizationRequirement?.anyRoles,
      anyPermissions: this.authorizationRequirement?.anyPermissions,
      allPermissions: this.authorizationRequirement?.allPermissions,
    }, user.currentOrganization?.id ?? user.organization?.id);
    const hasFeatureFlags = !hasFeatureFlagRequirement
      || this.requiredFeatureFlags!.every((flag) => user.featureFlags.includes(flag));

    return isAuthorized && hasFeatureFlags;
  }

  isChildActive(): boolean {
    if (this.items) {
      return this.items.some((item) => item.active || item.isChildActive());
    }
    return false;
  }
}
